/**
 * AddToPlaylistButton - popover to add the current track to one of the user's
 * playlists (or quick-create one). Owner can add to any of their playlists too.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListPlus, Plus, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AddToPlaylistButton = ({ trackId, variant = 'pill' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [added, setAdded] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/artists/playlists`, { headers: headers() });
      setPlaylists((res.data.playlists || []).filter((p) => !p.is_my_vibes));
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const addTo = async (pl) => {
    try {
      await axios.post(`${API_URL}/api/artists/playlists/${pl.id}/tracks`, { track_id: trackId }, { headers: headers() });
      setAdded((a) => ({ ...a, [pl.id]: true }));
      toast.success(`Added to "${pl.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add');
    }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/artists/playlists`, { name: newName.trim(), is_public: true }, { headers: headers() });
      const pl = res.data.playlist;
      await axios.post(`${API_URL}/api/artists/playlists/${pl.id}/tracks`, { track_id: trackId }, { headers: headers() });
      toast.success(`Created "${pl.name}" & added song`);
      setNewName('');
      setPlaylists((p) => [{ ...pl, track_count: 1 }, ...p]);
      setAdded((a) => ({ ...a, [pl.id]: true }));
    } catch (e) {
      toast.error('Could not create playlist');
    } finally { setCreating(false); }
  };

  return (
    <div className="relative">
      {variant === 'icon' ? (
        <button
          onClick={toggle}
          className={`flex flex-col items-center gap-1 transition-colors ${open ? 'text-[#00F0FF]' : 'text-[#A1A1AA] hover:text-white'}`}
          data-testid="add-to-playlist-btn"
          type="button"
        >
          <span className={`p-1.5 rounded-xl transition-colors ${open ? 'bg-[#00F0FF]/15' : ''}`}>
            <ListPlus className="w-6 h-6" />
          </span>
          <span className="text-xs">Playlist</span>
        </button>
      ) : (
        <motion.button
          whileTap={{ scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          onClick={toggle}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00F0FF]/15 hover:bg-[#00F0FF]/25 active:bg-[#00F0FF]/35 transition-colors text-xs font-medium text-white"
          data-testid="add-to-playlist-btn"
          type="button"
        >
          <ListPlus className="w-4 h-4 text-[#00F0FF]" /> Playlist
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-[64]" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-[65] w-64 bg-[#12101A] border border-white/10 rounded-2xl p-2 shadow-2xl"
              data-testid="add-to-playlist-popover"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-2 py-1">
                <p className="text-white text-xs font-semibold">Add to playlist</p>
                <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10 text-white/50"><X className="w-4 h-4" /></button>
              </div>

              <div className="max-h-52 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" /></div>
                ) : playlists.length === 0 ? (
                  <p className="text-white/40 text-xs px-2 py-3 text-center">No playlists yet — create one below.</p>
                ) : (
                  playlists.map((pl) => (
                    <button
                      key={pl.id}
                      onClick={() => addTo(pl)}
                      disabled={added[pl.id]}
                      className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left"
                      data-testid={`add-to-playlist-option-${pl.id}`}
                    >
                      <span className="text-white text-sm truncate">{pl.name}</span>
                      {added[pl.id]
                        ? <Check className="w-4 h-4 text-[#39FF14] shrink-0" />
                        : <Plus className="w-4 h-4 text-white/50 shrink-0" />}
                    </button>
                  ))
                )}
              </div>

              <div className="flex items-center gap-1 mt-1 border-t border-white/10 pt-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createAndAdd()}
                  placeholder="New playlist…"
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00F0FF]"
                  data-testid="add-to-playlist-new-input"
                />
                <button
                  onClick={createAndAdd}
                  disabled={!newName.trim() || creating}
                  className="p-2 rounded-lg bg-[#00F0FF] text-[#050505] disabled:opacity-40"
                  data-testid="add-to-playlist-create-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddToPlaylistButton;
